import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useSearchParams } from "react-router-dom";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Plus, Flame, AlertTriangle, Check, X } from "lucide-react";

export default function Challenges() {
  const [memberships, setMemberships] = useState([]);
  const [charities, setCharities] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [openCreate, setOpenCreate] = useState(false);
  const [active, setActive] = useState(null);
  const [days, setDays] = useState([]);

  const load = async () => {
    // Auto-verify past days first (75 Hard: missed day → charity)
    try {
      const t = await api.post("/challenges/tick");
      if (t.data?.forfeited?.length) {
        t.data.forfeited.forEach((f) => {
          toast.error(`Stake forfeited — $${f.amount} donated to charity (missed ${f.failed_date})`);
        });
      }
    } catch (e) {}
    const r = await api.get("/challenges/my");
    setMemberships(r.data);
    if (r.data.length && !active) {
      setActive(r.data[0]);
    }
  };

  useEffect(() => {
    load();
    api.get("/challenges/charities").then((r) => setCharities(r.data)).catch(() => {});
    api.get("/challenges/templates").then((r) => setTemplates(r.data)).catch(() => {});
  }, []);

  const [params, setParams] = useSearchParams();
  useEffect(() => {
    const status = params.get("status");
    const sid = params.get("session_id");
    if (status === "success" && sid) {
      const start = Date.now();
      const iv = setInterval(async () => {
        try {
          const r = await api.get(`/payments/status/${sid}`);
          if (r.data.payment_status === "paid") {
            clearInterval(iv);
            toast.success("Stake confirmed — you're in.");
            setParams({});
            load();
          } else if (Date.now() - start > 20000) {
            clearInterval(iv);
            setParams({});
          }
        } catch {}
      }, 2000);
      return () => clearInterval(iv);
    } else if (status === "cancel") {
      toast("Stake cancelled");
      setParams({});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params]);

  useEffect(() => {
    if (active?.membership?.id) {
      api.get(`/challenges/${active.membership.id}/days`).then((r) => setDays(r.data)).catch(() => {});
    }
  }, [active]);

  const checkoff = async (day, key, val) => {
    const tasks = { ...(day.tasks || {}), [key]: val };
    try {
      await api.post("/challenges/checkoff", {
        membership_id: active.membership.id,
        date: day.date,
        tasks,
      });
      const r = await api.get(`/challenges/${active.membership.id}/days`);
      setDays(r.data);
      toast.success("Day updated");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Update failed");
    }
  };

  const forfeit = async () => {
    if (!confirm("Forfeit this challenge? Your stake will go to charity.")) return;
    try {
      const r = await api.post(`/challenges/${active.membership.id}/forfeit`);
      toast.success(`$${r.data.donated_to_charity} donated to charity`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Forfeit failed");
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-6 md:px-10 py-10 md:py-14 space-y-10" data-testid="challenges-page">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="eyebrow">STAKES</div>
          <h1 className="font-display text-5xl md:text-7xl uppercase mt-2 leading-none">Put it on the line.</h1>
        </div>
        <button onClick={() => setOpenCreate(true)} className="btn-lime" data-testid="create-challenge-btn">
          <Plus className="w-4 h-4" /> New Stake
        </button>
      </div>

      {memberships.length === 0 ? (
        <div className="panel p-10 rounded-2xl text-center">
          <div className="font-display text-3xl uppercase mb-2">No stakes yet</div>
          <p className="text-neutral-600 mb-6">Pick a challenge, choose a charity, lock in your stake.</p>
          <button onClick={() => setOpenCreate(true)} className="btn-lime">Start 75 Hard</button>
        </div>
      ) : (
        <div className="grid md:grid-cols-3 gap-4">
          {memberships.map((m) => (
            <button
              key={m.membership.id}
              onClick={() => setActive(m)}
              data-testid={`challenge-card-${m.membership.id}`}
              className={`text-left panel p-6 rounded-2xl border transition-all ${active?.membership?.id === m.membership.id ? "border-obsidian -translate-y-0.5" : "border-neutral-200 hover:-translate-y-0.5"}`}
            >
              <div className="flex items-center justify-between mb-3">
                <span className="font-mono text-[10px] tracking-[0.25em] uppercase text-neutral-500">{m.challenge?.name || "—"}</span>
                <StatusPill status={m.membership.status} />
              </div>
              <div className="flex items-baseline gap-1">
                <span className="font-display text-4xl">${m.membership.stake_amount.toFixed(0)}</span>
                <span className="font-mono text-xs uppercase tracking-widest text-neutral-500">staked</span>
              </div>
              <div className="mt-4 flex items-center justify-between text-sm">
                <div>
                  <span className="font-display text-2xl">{m.membership.days_completed}</span>
                  <span className="text-neutral-500 font-mono text-xs ml-1">/ {m.challenge?.duration_days || 75} DAYS</span>
                </div>
                <span className="text-xs text-neutral-500 font-mono tracking-widest uppercase">{m.charity?.name?.slice(0, 18) || "—"}</span>
              </div>
            </button>
          ))}
        </div>
      )}

      {active && days.length > 0 && (
        <div className="panel p-7 rounded-2xl">
          <div className="flex items-center justify-between flex-wrap gap-4 mb-6">
            <div>
              <div className="eyebrow mb-1">{active.challenge?.name}</div>
              <div className="font-display text-3xl">{active.challenge?.start_date} → {active.challenge?.end_date}</div>
            </div>
            {active.membership.status === "active" && (
              <button onClick={forfeit} className="font-mono text-xs uppercase tracking-[0.2em] text-neutral-500 hover:text-red-600 transition-colors" data-testid="forfeit-btn">
                <AlertTriangle className="w-4 h-4 inline mr-1" /> Forfeit
              </button>
            )}
          </div>

          <div className="grid grid-cols-7 md:grid-cols-15 gap-1.5" data-testid="day-grid" style={{ gridTemplateColumns: "repeat(15, minmax(0, 1fr))" }}>
            {days.map((d, i) => (
              <DayCell key={d.date || i} day={d} onClick={() => active.membership.status === "active" && d.verified_by !== "future" && expand(d, checkoff)} />
            ))}
          </div>

          <div className="mt-6 flex items-center gap-4 font-mono text-[10px] uppercase tracking-[0.2em] text-neutral-500 flex-wrap">
            <div className="flex items-center gap-2"><div className="w-3 h-3 rounded-sm bg-lime" /> Verified</div>
            <div className="flex items-center gap-2"><div className="w-3 h-3 rounded-sm bg-red-500" /> Failed</div>
            <div className="flex items-center gap-2"><div className="w-3 h-3 rounded-sm bg-neutral-200" /> Pending / Future</div>
          </div>

          <TasksToday day={days.find((d) => d.date === new Date().toISOString().slice(0, 10))}
                     onToggle={(k, v) => {
                       const today = days.find((d) => d.date === new Date().toISOString().slice(0, 10));
                       if (today) checkoff(today, k, v);
                     }}
                     status={active.membership.status}
                     template={templates.find((t) => t.id === active.challenge?.template) || templates[0]}
          />
        </div>
      )}

      <CreateDialog open={openCreate} onOpenChange={setOpenCreate} charities={charities} templates={templates} onCreated={load} />
    </div>
  );
}

function expand(/*day, checkoff*/) {
  // could open a per-day detail dialog; current UX uses today's task panel below
}

function StatusPill({ status }) {
  const map = {
    active: { label: "ACTIVE", bg: "bg-lime", text: "text-obsidian" },
    completed: { label: "COMPLETED", bg: "bg-obsidian", text: "text-white" },
    failed: { label: "FAILED", bg: "bg-red-500", text: "text-white" },
    forfeited: { label: "FORFEITED", bg: "bg-red-500", text: "text-white" },
  };
  const m = map[status] || map.active;
  return <span className={`text-[10px] font-mono uppercase tracking-[0.25em] px-2 py-1 rounded-full ${m.bg} ${m.text}`}>{m.label}</span>;
}

function DayCell({ day, onClick }) {
  let bg = "bg-neutral-200";
  let title = day.date;
  if (day.verified) { bg = "bg-lime"; title += " · verified"; }
  else if (day.failed) { bg = "bg-red-500"; title += " · failed"; }
  else if (day.verified_by === "future") { bg = "bg-neutral-100"; }
  return (
    <button
      onClick={onClick}
      title={title}
      data-testid={`day-cell-${day.date}`}
      className={`aspect-square rounded ${bg} hover:scale-110 transition-transform`}
    />
  );
}

function TasksToday({ day, onToggle, status, template }) {
  if (!day || status !== "active") return null;
  const tasks = template?.tasks || [
    { key: "workout_1", label: "Workout 1 (45 min)", auto: true },
    { key: "workout_2", label: "Workout 2 (45 min, outdoors)", auto: true },
    { key: "water", label: "1 gallon of water", auto: false },
    { key: "reading", label: "10 pages of non-fiction", auto: false },
    { key: "diet", label: "No alcohol / cheat meals", auto: false },
    { key: "photo", label: "Daily progress photo", auto: false },
  ];
  return (
    <div className="mt-8 pt-8 border-t border-neutral-200">
      <div className="eyebrow mb-4">TODAY'S TASKS</div>
      <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-3">
        {tasks.map((t) => {
          const checked = !!day.tasks?.[t.key];
          return (
            <button
              key={t.key}
              onClick={() => onToggle(t.key, !checked)}
              data-testid={`task-${t.key}`}
              className={`flex items-center gap-3 p-4 rounded-xl border transition-all text-left ${checked ? "border-obsidian bg-obsidian text-white" : "border-neutral-200 hover:border-neutral-400 bg-white"}`}
            >
              <div className={`w-6 h-6 rounded-full flex items-center justify-center shrink-0 ${checked ? "bg-lime" : "border border-neutral-300"}`}>
                {checked ? <Check className="w-4 h-4 text-obsidian" /> : null}
              </div>
              <div>
                <div className="font-mono text-xs uppercase tracking-widest">{t.label}</div>
                {t.auto && <div className="text-[10px] mt-0.5 opacity-60">AUTO via WHOOP</div>}
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}

function CreateDialog({ open, onOpenChange, charities, templates, onCreated }) {
  const [name, setName] = useState("75 Hard");
  const [template, setTemplate] = useState("75_hard");
  const [stake, setStake] = useState(100);
  const [charity, setCharity] = useState("givewell");
  const [loading, setLoading] = useState(false);

  const submit = async () => {
    setLoading(true);
    try {
      const r = await api.post("/challenges/create", {
        name, template, stake_amount: Number(stake), charity_id: charity,
        origin_url: window.location.origin,
      });
      if (r.data.checkout_url) {
        toast.success("Stake locked — redirecting to Stripe…");
        onOpenChange(false);
        window.location.href = r.data.checkout_url;
        return;
      }
      toast.success("Challenge created (Stripe not available — dev mode, no escrow).");
      onOpenChange(false);
      onCreated && onCreated();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't create challenge");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-white rounded-3xl border-neutral-200 max-w-lg p-8">
        <DialogHeader>
          <DialogTitle className="font-display text-4xl uppercase leading-none">New Stake</DialogTitle>
          <DialogDescription className="font-mono text-xs tracking-[0.2em] uppercase text-neutral-500 mt-2">
            Charity on loss — money never goes to other players.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 mt-4" data-testid="create-challenge-form">
          <div>
            <Label className="font-mono text-[10px] uppercase tracking-[0.2em] text-neutral-500">Challenge</Label>
            <Select value={template} onValueChange={setTemplate}>
              <SelectTrigger className="mt-1 rounded-xl border-neutral-300" data-testid="create-template-trigger"><SelectValue /></SelectTrigger>
              <SelectContent>
                {templates.map((t) => <SelectItem key={t.id} value={t.id}>{t.name} · {t.duration_days} days</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="font-mono text-[10px] uppercase tracking-[0.2em] text-neutral-500">Name</Label>
            <Input value={name} onChange={(e) => setName(e.target.value)} className="mt-1 rounded-xl border-neutral-300" data-testid="create-name" />
          </div>
          <div>
            <Label className="font-mono text-[10px] uppercase tracking-[0.2em] text-neutral-500">Stake ($USD)</Label>
            <Input type="number" min={20} max={2000} value={stake} onChange={(e) => setStake(e.target.value)} className="mt-1 rounded-xl border-neutral-300" data-testid="create-stake" />
            <div className="text-[10px] font-mono text-neutral-500 mt-1 uppercase tracking-widest">Held in Stripe escrow · refunded if you complete · charity on loss</div>
          </div>
          <div>
            <Label className="font-mono text-[10px] uppercase tracking-[0.2em] text-neutral-500">Charity on loss</Label>
            <Select value={charity} onValueChange={setCharity}>
              <SelectTrigger className="mt-1 rounded-xl border-neutral-300" data-testid="create-charity-trigger"><SelectValue /></SelectTrigger>
              <SelectContent>
                {charities.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>
        <DialogFooter className="mt-6">
          <button onClick={() => onOpenChange(false)} className="btn-outline-ink">Cancel</button>
          <button onClick={submit} disabled={loading} className="btn-lime" data-testid="create-submit">
            {loading ? "..." : "Lock Stake"}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
